const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const HAS_OFFSET_PATTERN = /(?:Z|[+-]\d{2}:?\d{2})$/;
export function isValidISODate(d) {
  if (!ISO_DAY_PATTERN.test(d)) return false;
  const [year, month, day] = d.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}
export function isValidTimeZone(timeZone) {
  try {
    Intl.DateTimeFormat(undefined, {
      timeZone
    }).format();
    return true;
  } catch (_unused) {
    return false;
  }
}
export function resolveTimeZone(timeZone) {
  return timeZone && isValidTimeZone(timeZone) ? timeZone : 'UTC';
}

// Parse a supported instant into a Date, or undefined when the input can't be
// resolved to an absolute moment. Accepts epoch-ms numbers (finite and within
// the representable Date range), ISO days, and offset-bearing ISO datetimes.
// Naive datetimes are rejected on purpose — without an offset they don't pin an
// absolute instant.
export function toInstant(input) {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return undefined;
    const date = new Date(input);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  if (typeof input !== 'string') return undefined;
  // Scalar context values arrive stringified, so a canonical integer string is
  // treated as epoch ms — keeping numbers and their string form interchangeable.
  if (/^-?\d+$/.test(input)) return toInstant(Number(input));
  if (!ISO_DAY_PATTERN.test(input) && !HAS_OFFSET_PATTERN.test(input)) {
    return undefined;
  }
  const ms = Date.parse(input);
  return Number.isNaN(ms) ? undefined : new Date(ms);
}

// The supported forms for a single availability entry. A day input is either an
// epoch-ms instant (number), an ISO day (`YYYY-MM-DD`), or an ISO datetime that
// pins an absolute instant with an offset/`Z`. Naive datetimes are excluded on
// purpose — without an offset they can't be resolved to a day deterministically.
export function isSupportedDateInput(input) {
  if (typeof input === 'number') {
    return Number.isFinite(input) && !Number.isNaN(new Date(input).getTime());
  }
  if (typeof input === 'object' && input !== null && typeof input.startMillisUtc === 'number' && !Number.isNaN(new Date(input.startMillisUtc).getTime())) {
    return true;
  }
  if (typeof input !== 'string') return false;
  if (isValidISODate(input)) return true;
  return HAS_OFFSET_PATTERN.test(input) && !Number.isNaN(Date.parse(input));
}

// Produces a canonical ISO-8601 day key (never a display string), so the
// output must stay locale-independent: always Gregorian, always Latin digits.
function formatInstantAsDay(epochMs, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    calendar: 'gregory',
    numberingSystem: 'latn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(epochMs));
  const partValue = type => {
    var _parts$find$value, _parts$find;
    return (_parts$find$value = (_parts$find = parts.find(part => part.type === type)) === null || _parts$find === void 0 ? void 0 : _parts$find.value) !== null && _parts$find$value !== void 0 ? _parts$find$value : '';
  };
  return `${partValue('year')}-${partValue('month')}-${partValue('day')}`;
}
export function toDayString(input, timeZone) {
  if (!isSupportedDateInput(input)) return undefined;
  if (typeof input === 'number') return formatInstantAsDay(input, timeZone);
  if (typeof input === 'object') return formatInstantAsDay(input.startMillisUtc, timeZone);
  if (ISO_DAY_PATTERN.test(input)) return input;
  return formatInstantAsDay(Date.parse(input), timeZone);
}
export function toDayStrings(inputs, timeZone) {
  const days = inputs.map(input => toDayString(input, timeZone)).filter(day => day !== undefined);
  return Array.from(new Set(days));
}